import { memo, type CSSProperties } from 'react';
import { domainIcon, domainTone, isTeams } from '@jobfair/shared';
import { useTrack } from '../lib/trackerContext';
import type { Access, Unit } from '../types';
import { ExtLink } from './common';
import { Icon } from './Icon';

interface Props {
  u: Unit;
  access: Access;
  fav: boolean;
  onFav: (id: string) => void;
  onOpen: (id: string) => void;
  idx: number;
}

/** Memoized: the page ticks every second, but a card only changes when its own props do. */
export const UnitCard = memo(function UnitCard({ u, access, fav, onFav, onOpen, idx }: Props) {
  const track = useTrack();
  let action;
  if (access.teamsOpen && isTeams(u.teams)) {
    action = (
      <ExtLink
        className="u-join"
        href={u.teams}
        label={`מפגש Teams של ${u.name}`}
        onOpen={() => track('join_click', { unitId: u.id })}
      >
        <span className="dot" aria-hidden="true" />
        הצטרפות למפגש
      </ExtLink>
    );
  } else if (access.teamsOpen) {
    action = (
      <span className="u-note">
        <Icon n="info" s={15} />
        אין מפגש זמין
      </span>
    );
  } else if (access.ended) {
    action = (
      <span className="u-note">
        <Icon n="check" s={15} />
        המפגשים הסתיימו
      </span>
    );
  } else {
    action = (
      <span className="u-note">
        <Icon n="clock" s={15} />
        המפגש ייפתח ב-<span className="num">{access.r1}</span>
      </span>
    );
  }

  const meta = [
    u.region,
    u.pdfs.length ? (u.pdfs.length === 1 ? 'PDF' : u.pdfs.length + ' PDF') : '',
    u.youtube ? 'סרטון' : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article
      className="ucard"
      style={
        {
          '--dc': domainTone(u.domain),
          animationDelay: Math.min(idx, 14) * 30 + 'ms',
        } as CSSProperties
      }
      aria-labelledby={'t-' + u.id}
    >
      <div className="u-top">
        <span className={'u-mark' + (u.symbol ? ' has-logo' : '')} aria-hidden="true">
          {u.symbol ? (
            <img src={u.symbol} alt="" loading="lazy" />
          ) : (
            <Icon n={domainIcon(u.domain)} s={24} />
          )}
        </span>
        <span className="u-side">
          {u.booth ? (
            <span className="u-no">
              <small>דוכן</small>
              <b>{u.booth}</b>
            </span>
          ) : null}
          <button
            className="fav"
            aria-pressed={fav ? 'true' : 'false'}
            aria-label={(fav ? 'הסרה מהמועדפים: ' : 'שמירה במועדפים: ') + u.name}
            title={fav ? 'הסרה מהמועדפים' : 'שמירה במועדפים'}
            onClick={() => onFav(u.id)}
          >
            <Icon n="star" s={16} fill={fav} />
          </button>
        </span>
      </div>
      {u.domain && <span className="u-dom">{u.domain}</span>}
      <h3 className="card-title" id={'t-' + u.id}>
        <button className="card-open" onClick={() => onOpen(u.id)} aria-haspopup="dialog">
          {u.name}
        </button>
      </h3>
      {(u.nameAr || u.nameEn) && (
        <div className="u-alt">
          {u.nameAr && (
            <span lang="ar" dir="rtl">
              {u.nameAr}
            </span>
          )}
          {u.nameEn && (
            <span lang="en" dir="ltr">
              {u.nameEn}
            </span>
          )}
        </div>
      )}
      {meta && <div className="u-meta">{meta}</div>}
      <div className="u-foot">
        {action}
        <span className="more" aria-hidden="true">
          פרטים ←
        </span>
      </div>
    </article>
  );
});
